import { useMemo } from 'react';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
}

const rules = [
  { label: '8 caractères minimum', test: (p: string) => p.length >= 8 },
  { label: 'Une majuscule', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Une minuscule', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Un chiffre', test: (p: string) => /[0-9]/.test(p) },
];

export const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  const passed = useMemo(() => rules.filter(r => r.test(password)).length, [password]);

  if (!password) return null;

  const strength = passed <= 1 ? 'Faible' : passed <= 2 ? 'Moyen' : passed <= 3 ? 'Bon' : 'Fort';
  const barColor = passed <= 1 ? 'bg-destructive' : passed <= 2 ? 'bg-orange-500' : passed <= 3 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="space-y-2 mt-2">
      {/* Bar */}
      <div className="flex gap-1">
        {[0, 1, 2, 3].map(i => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i < passed ? barColor : 'bg-muted'}`}
          />
        ))}
      </div>
      <p className={`text-xs font-medium ${passed <= 1 ? 'text-destructive' : passed <= 2 ? 'text-orange-500' : passed <= 3 ? 'text-yellow-600' : 'text-green-600'}`}>
        {strength}
      </p>
      {/* Rules */}
      <ul className="space-y-1">
        {rules.map((rule, i) => {
          const ok = rule.test(password);
          return (
            <li key={i} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-muted-foreground'}`}>
              {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              {rule.label}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
